import { ALIGNMENT_FILTER, ALIGNMENT_STATE, LISTING_FILTER, RUN_KIND, RUN_STATUS, STORE_SALE_FILTER } from './stockSyncConstants';
import { formatQuantity } from '../../utils/inventoryLabels';
import { normalizeText } from '../../utils/text';

/** `modasur.myshopify.com`, however the store's address was typed. */
export const shopDomain = (url) =>
  String(url || '')
    .replace(/^https?:\/\//, '')
    .replace(/\/+$/, '')
    .trim();

/** How a store reads in a picker: its client and its address. A client has one Shopify store. */
export const channelLabel = (row) => {
  const label = [row?.channel?.company?.name, shopDomain(row?.channel?.shop_url)].filter(Boolean).join(' · ');
  return label || `Tienda #${row?.channel?.id}`;
};

/** The stores whose client keeps its stock here with the WMS: without it there is no stock to sync. */
export const usableChannels = (channels) => channels.filter((row) => row?.wms?.lite === true);

/**
 * The store a dialog opens on: the one already in use, the one of the client
 * the page is filtered by, or the only one. With several and no clue, none:
 * the person chooses, rather than syncing a store they did not mean.
 *
 * @param {Array<object>} channels - Usable ones.
 * @param {{selectedId?: number|null, companyId?: number|string|null}} [context]
 * @returns {number|null}
 */
export const pickDefaultChannelId = (channels, { selectedId = null, companyId = null } = {}) => {
  if (selectedId && channels.some((row) => row.channel.id === selectedId)) return selectedId;
  if (companyId !== null && companyId !== undefined) {
    const ofClient = channels.find((row) => String(row.channel.company?.id) === String(companyId));
    if (ofClient) return ofClient.channel.id;
  }
  return channels.length === 1 ? channels[0].channel.id : null;
};

export const isRunActive = (report) =>
  report?.status === RUN_STATUS.QUEUED || report?.status === RUN_STATUS.RUNNING;

/**
 * What stands between the store and the sync: permissions it never granted
 * (`missing`: it has to authorize Ninja again), or a store that did not say
 * (`unanswered`). `null` when nothing is known to be wrong.
 *
 * @param {object|null} detail
 * @returns {{kind: 'missing', scopes: string[]}|{kind: 'unanswered', error: string}|null}
 */
export const scopesProblemOf = (detail) => {
  if (!detail) return null;
  if (Array.isArray(detail.missing_scopes) && detail.missing_scopes.length > 0) {
    return { kind: 'missing', scopes: detail.missing_scopes };
  }
  if (detail.missing_scopes === null && detail.scopes_error) {
    return { kind: 'unanswered', error: detail.scopes_error };
  }
  return null;
};

const needsAuthorization = (detail) => scopesProblemOf(detail)?.kind === 'missing';

/**
 * The permissions confirming the comparison needs besides the sync's own, or
 * `null`: following the store's own sales reads its orders. A store that did
 * not answer is told by `scopesProblemOf`.
 */
export const alignmentScopesProblemOf = (detail) =>
  Array.isArray(detail?.missing_alignment_scopes) && detail.missing_alignment_scopes.length > 0
    ? { kind: 'missing', scopes: detail.missing_alignment_scopes }
    : null;

const SCOPE_LABELS = {
  read_products: 'leer sus productos',
  read_inventory: 'leer su inventario',
  write_inventory: 'publicar stock',
  read_locations: 'leer sus ubicaciones',
  read_orders: 'leer sus órdenes',
  read_merchant_managed_fulfillment_orders: 'ver qué le falta despachar a cada orden y desde qué ubicación',
};

/**
 * The permissions the store never granted, said as what Ninja cannot do
 * without them; `forActivation` when only confirming the comparison needs them.
 */
export const missingScopesText = (scopes, { forActivation = false } = {}) => {
  const labels = scopes.map((scope) => SCOPE_LABELS[scope] || scope);
  const list = labels.length > 1 ? `${labels.slice(0, -1).join(', ')} y ${labels[labels.length - 1]}` : labels[0];
  const lead = forActivation
    ? 'Para confirmar la publicación y descontar las ventas de la tienda, la tienda tiene que volver a autorizar a Ninja'
    : 'La tienda tiene que volver a autorizar a Ninja';
  return `${lead}: le faltan permisos para ${list}.`;
};

export const formatSyncDate = (value) =>
  value ? new Date(value).toLocaleString('es-CL', { dateStyle: 'short', timeStyle: 'short' }) : '';

/** «3:40 p. m.»: when a comparison stops being good, the same day it was made, as `formatSyncDate` says the hour. */
export const formatSyncTime = (value) =>
  value ? new Date(value).toLocaleTimeString('es-CL', { timeStyle: 'short' }) : '';

/** Ends a sentence on what it was given, once: «p. m.» already brings its period. */
export const withPeriod = (text) => (text.endsWith('.') ? text : `${text}.`);

const RUNNING_TEXT = {
  [RUN_KIND.CATALOG]: 'Leyendo la tienda…',
  [RUN_KIND.STOCK_PREVIEW]: 'Comparando con la tienda…',
  [RUN_KIND.PUBLISH]: 'Publicando el stock…',
};

/**
 * The line a run's card leads with: where it stands, and since when.
 *
 * @returns {{status: string, tone: string, text: string, at: string}|null}
 */
export const runHeadline = (kind, report) => {
  if (!report) return null;
  switch (report.status) {
    case RUN_STATUS.QUEUED:
      return { status: report.status, tone: 'info', text: 'En cola…', at: report.queued_at };
    case RUN_STATUS.RUNNING:
      return { status: report.status, tone: 'info', text: RUNNING_TEXT[kind], at: report.started_at };
    case RUN_STATUS.DONE:
      return { status: report.status, tone: 'success', text: 'Terminó', at: report.finished_at };
    case RUN_STATUS.FAILED:
      return { status: report.status, tone: 'danger', text: 'Falló', at: report.finished_at };
    default:
      return null;
  }
};

const counted = (count, one, many) => `${formatQuantity(count ?? 0)} ${count === 1 ? one : many}`;

/** «1 producto», «318 productos»: for the confirmations, which count what they touch. */
export const productCount = (count) => counted(count, 'producto', 'productos');

/** How many linked products publishing reaches: the ones not paused, counted in ok or error. */
export const publishedCount = (listings) => (listings?.ok ?? 0) + (listings?.error ?? 0);

/** What a finished catalog sync did, a line per kind of change. */
export const catalogResultLines = (result) => {
  if (!result) return [];
  return [
    { key: 'variants', text: counted(result.variants, 'variante leída en la tienda', 'variantes leídas en la tienda') },
    { key: 'created', text: counted(result.created, 'producto nuevo en Ninja', 'productos nuevos en Ninja') },
    {
      key: 'updated',
      text: counted(result.updated, 'producto completado (EAN o peso)', 'productos completados (EAN o peso)'),
    },
    {
      key: 'links',
      text: `${counted(result.linked, 'vínculo nuevo', 'vínculos nuevos')} · ${counted(
        result.unlinked,
        'vínculo quitado',
        'vínculos quitados'
      )}`,
    },
    {
      key: 'listed',
      text: counted(result.listed, 'producto vinculado a la tienda', 'productos vinculados a la tienda'),
    },
  ];
};

/** «40 productos suben y 12 bajan»: what confirming changes in the store; `null` when nothing does. */
const storeMovesText = ({ up = 0, down = 0 } = {}) => {
  const rises = up === 1 ? 'sube' : 'suben';
  const falls = down === 1 ? 'baja' : 'bajan';
  if (up && down) return `${productCount(up)} ${rises} y ${formatQuantity(down)} ${falls}`;
  if (up) return `${productCount(up)} ${rises}`;
  if (down) return `${productCount(down)} ${falls}`;
  return null;
};

/** What confirming leaves out of the store, each kind its own part: «3 sin activar en la ubicación», «1 pausado». */
const leftOutParts = (summary) =>
  [
    summary?.not_stocked ? counted(summary.not_stocked, 'sin activar en la ubicación', 'sin activar en la ubicación') : null,
    summary?.virtual_kit ? counted(summary.virtual_kit, 'kit virtual', 'kits virtuales') : null,
    summary?.paused ? counted(summary.paused, 'pausado', 'pausados') : null,
  ].filter(Boolean);

const leftOutLead = (summary) =>
  (summary?.not_stocked ?? 0) + (summary?.virtual_kit ?? 0) + (summary?.paused ?? 0) === 1 ? 'Queda fuera' : 'Quedan fuera';

/** «a, b y c». */
const listText = (parts) => (parts.length > 1 ? `${parts.slice(0, -1).join(', ')} y ${parts[parts.length - 1]}` : parts[0]);

/** What a finished comparison found: what the store takes of Ninja, and what it leaves out. */
export const previewSummaryLines = (summary) => {
  if (!summary) return [];
  const lines = [
    {
      key: 'compared',
      text: `${counted(summary.total, 'producto comparado', 'productos comparados')} · ${counted(
        summary.same,
        'ya igual',
        'ya iguales'
      )}`,
    },
  ];
  const moves = storeMovesText(summary);
  lines.push(
    moves ? { key: 'moves', text: `${moves} en la tienda` } : { key: 'nothing', text: 'Ningún producto cambia en la tienda.' }
  );
  const left = leftOutParts(summary);
  if (left.length) lines.push({ key: 'attention', tone: 'warning', text: `${leftOutLead(summary)}: ${left.join(' · ')}` });
  return lines;
};

/** What a finished publication left in the store, a line per kind of product. */
export const publishResultLines = (result) => {
  if (!result) return [];
  const lines = [
    {
      key: 'published',
      text: `${counted(result.set, 'producto tomó el número de Ninja', 'productos tomaron el número de Ninja')} · ${counted(
        result.same,
        'ya lo tenía',
        'ya lo tenían'
      )}`,
    },
  ];
  if (result.not_stocked) {
    lines.push({
      key: 'not_stocked',
      tone: 'warning',
      text:
        result.not_stocked === 1
          ? '1 producto no está activo en la ubicación de la tienda: no se publica'
          : `${formatQuantity(result.not_stocked)} productos no están activos en la ubicación de la tienda: no se publican`,
    });
  }
  if (result.stale) {
    lines.push({
      key: 'stale',
      text:
        result.stale === 1
          ? '1 producto cambió en la tienda mientras se publicaba: se publica en su turno'
          : `${formatQuantity(result.stale)} productos cambiaron en la tienda mientras se publicaban: se publican en su turno`,
    });
  }
  if (result.errors) {
    lines.push({
      key: 'errors',
      tone: 'danger',
      text: `${counted(result.errors, 'producto con error', 'productos con error')}${result.error ? `: ${result.error}` : ''}`,
    });
  }
  return lines;
};

/** The variants a run left out. The report keeps five hundred at most; `total` says how many there were. */
export const skippedOf = (result) => ({
  total: result?.skipped_total ?? 0,
  rows: result?.skipped ?? [],
});

/** Why «Sincronizar SKUs» cannot go now, or `null`. */
export const catalogSyncBlocker = (detail) => {
  if (!detail) return 'Elige la tienda';
  if (needsAuthorization(detail)) return 'La tienda tiene que volver a autorizar a Ninja';
  if (isRunActive(detail.runs?.[RUN_KIND.CATALOG])) return 'Ya hay una sincronización corriendo';
  return null;
};

/** Whether this person activates the store's publication here: who stores in the warehouse, with the WMS. */
export const canSyncStock = (detail) => Boolean(detail?.can_sync_stock && detail?.wms?.lite);

/** Whether Ninja publishes in the store: switched on, and its comparison confirmed. */
export const isPublishing = (detail) => Boolean(detail?.enabled && detail?.activated_at);

/** Why «Comparar con la tienda» cannot go now, or `null`. Comparing only reads the store's stock. */
export const stockCompareBlocker = (detail) => {
  if (needsAuthorization(detail)) return 'La tienda tiene que volver a autorizar a Ninja';
  if (!detail.location) return 'Elige primero la ubicación de la tienda';
  if (isRunActive(detail.runs?.[RUN_KIND.STOCK_PREVIEW])) return 'Ya hay una comparación corriendo';
  if (!detail.listings?.total) return 'Todavía no hay productos vinculados: sincroniza los SKUs en Productos';
  return null;
};

/**
 * Why publishing cannot be turned on from its switch, or `null`. Turning it
 * off is always possible; on, it goes through the comparison and its
 * confirmation, which publishes every product.
 */
export const publishBlocker = (detail) => {
  if (detail.enabled) return null;
  if (needsAuthorization(detail)) return 'La tienda tiene que volver a autorizar a Ninja';
  if (!detail.location) return 'Elige primero la ubicación de la tienda';
  if (!canSyncStock(detail)) return 'La activa quien guarda la mercadería en esta bodega, con el WMS';
  return 'Compara con la tienda y confirma para encenderla';
};

/** What moving the location does while publishing is on, said before it goes. */
export const locationChangeMessage = (detail) =>
  isPublishing(detail)
    ? `Ninja deja de publicar el stock de ${productCount(publishedCount(detail.listings))} hasta que compares y confirmes en la ubicación nueva.`
    : 'Para publicar en la ubicación nueva, compara con la tienda y confirma.';

/**
 * The store's locations to choose from: the active ones, plus the one in use
 * when the store no longer lists it, so the picker still says where it is.
 */
export const locationOptions = (locations, current) => {
  const options = locations
    .filter((location) => location.active)
    .map((location) => ({ value: location.id, label: location.name }));
  if (current && !options.some((option) => option.value === current.id)) {
    options.unshift({ value: current.id, label: current.name });
  }
  return options;
};

/** The filter chips over the linked products, with what the server counted. */
export const listingFilterOptions = (counts) => [
  { value: LISTING_FILTER.OK, label: 'Sin errores', count: counts?.ok, tone: 'success' },
  { value: LISTING_FILTER.ERROR, label: 'Con error', count: counts?.error, tone: 'danger' },
  { value: LISTING_FILTER.DISABLED, label: 'Pausados', count: counts?.disabled, tone: 'neutral' },
];

/** The line under «Publicar»: how the linked products stand. */
export const publishSummary = (counts) =>
  [
    counted(counts?.ok, 'sin errores', 'sin errores'),
    counted(counts?.error, 'con error', 'con error'),
    counted(counts?.disabled, 'pausado', 'pausados'),
  ].join(' · ');

/**
 * Where one linked product stands with the store. A paused one says so
 * first, and a virtual kit, which has no stock of its own: nothing else
 * about them moves.
 *
 * @returns {{key: string, label: string, tone: string}}
 */
export const listingStateOf = (row) => {
  if (!row.sync_enabled) return { key: 'paused', label: 'Pausado', tone: 'neutral' };
  if (row.virtual_kit) return { key: 'virtual_kit', label: 'Kit virtual', tone: 'neutral' };
  if (row.push_pending) return { key: 'pending', label: 'En cola', tone: 'info' };
  if (row.last_sync_error) return { key: 'error', label: 'Error', tone: 'danger' };
  if (row.last_synced_at) return { key: 'synced', label: 'Al día', tone: 'success' };
  return { key: 'unpublished', label: 'Sin publicar', tone: 'neutral' };
};

export const hasPendingListings = (rows) => rows.some((row) => row.sync_enabled && row.push_pending);

/**
 * Why «Publicar ahora» cannot go for one product, or `null`. The backend
 * skips the same ones, answering `queued: false`.
 */
export const pushBlocker = (row, detail) => {
  if (!detail?.enabled) return 'La publicación de la tienda está apagada';
  if (!detail.activated_at) return 'Confirma la comparación con la tienda para publicar';
  if (!row.sync_enabled) return 'Pausado: reanúdalo para publicarlo';
  if (row.virtual_kit) return 'Kit virtual: Ninja no lo publica';
  return null;
};

/**
 * What the toast says when a run ends, with the numbers it left.
 *
 * @returns {{type: 'success'|'error', message: string}|null}
 */
const RUN_NAME = {
  [RUN_KIND.CATALOG]: 'La sincronización de SKUs',
  [RUN_KIND.STOCK_PREVIEW]: 'La comparación con la tienda',
  [RUN_KIND.PUBLISH]: 'La publicación del stock',
};

export const runEndedNotice = (kind, report) => {
  if (report?.status === RUN_STATUS.FAILED) {
    return { type: 'error', message: `${RUN_NAME[kind]} falló: ${report.error || 'sin detalle'}` };
  }
  if (report?.status !== RUN_STATUS.DONE) return null;

  const result = report.result || {};
  if (kind === RUN_KIND.STOCK_PREVIEW) {
    const moves = storeMovesText(result.summary);
    const found = moves ? `${moves} en la tienda` : 'la tienda ya tiene el número de Ninja';
    return {
      type: 'success',
      message: `Comparación lista: ${found}. Confírmala antes de las ${withPeriod(formatSyncTime(result.expires_at))}`,
    };
  }

  if (kind === RUN_KIND.PUBLISH) {
    const published = result.set
      ? counted(result.set, 'producto tomó el número de Ninja', 'productos tomaron el número de Ninja')
      : 'la tienda ya tenía el número de Ninja';
    const errors = result.errors ? ` · ${counted(result.errors, 'con error', 'con error')}` : '';
    return { type: 'success', message: `Publicación lista: ${published}${errors}` };
  }

  const skipped = result.skipped_total ? ` · ${counted(result.skipped_total, 'omitido', 'omitidos')}` : '';
  return {
    type: 'success',
    message: `SKUs sincronizados: ${counted(result.created, 'producto nuevo', 'productos nuevos')} · ${counted(
      result.listed,
      'vinculado',
      'vinculados'
    )}${skipped}`,
  };
};

/* The comparison under review. */

export const PREVIEW_STATE = {
  NONE: 'none',
  RUNNING: 'running',
  FAILED: 'failed',
  READY: 'ready',
  EXPIRED: 'expired',
  APPLIED: 'applied',
};

/** What confirming a comparison sends back, and what tells two comparisons apart. */
export const previewTokenOf = (report) => report?.result?.token ?? null;

/** A comparison as the store's runs keep it: without its rows, which may weigh megabytes. */
export const previewSummaryOf = (report) => {
  if (!report?.result) return report ?? null;
  const { rows, aligned, ...result } = report.result;
  return { ...report, result };
};

/** A comparison is good for an hour; `expiredToken` is the one the clock already let go. */
export const isPreviewExpired = (report, expiredToken = null, now = Date.now()) => {
  const token = previewTokenOf(report);
  if (!token) return false;
  if (expiredToken === token) return true;
  const expiresAt = report.result?.expires_at;
  return Boolean(expiresAt) && Date.parse(expiresAt) <= now;
};

/** Where a comparison stands: none, on its way, failed, ready to confirm, expired or confirmed (`applied_at`). */
export const previewStateOf = (report, expiredToken = null, now = Date.now()) => {
  if (!report) return PREVIEW_STATE.NONE;
  if (isRunActive(report)) return PREVIEW_STATE.RUNNING;
  if (report.status === RUN_STATUS.FAILED) return PREVIEW_STATE.FAILED;
  if (report.applied_at) return PREVIEW_STATE.APPLIED;
  if (isPreviewExpired(report, expiredToken, now)) return PREVIEW_STATE.EXPIRED;
  return PREVIEW_STATE.READY;
};

/** How a row's state reads in its badge and its chip, and with which tone. */
const STATE_BADGE = {
  [ALIGNMENT_STATE.UP]: { label: 'Sube', tone: 'success' },
  [ALIGNMENT_STATE.DOWN]: { label: 'Baja', tone: 'warning' },
  [ALIGNMENT_STATE.SAME]: { label: 'Igual', tone: 'neutral' },
  [ALIGNMENT_STATE.NOT_STOCKED]: { label: 'Sin activar', tone: 'warning' },
  [ALIGNMENT_STATE.VIRTUAL_KIT]: { label: 'Kit virtual', tone: 'neutral' },
  [ALIGNMENT_STATE.PAUSED]: { label: 'Pausado', tone: 'neutral' },
};

/** The states whose product the store takes: the others stay out of it. */
const PUBLISHED_STATES = [ALIGNMENT_STATE.UP, ALIGNMENT_STATE.DOWN, ALIGNMENT_STATE.SAME];

export const isAlignmentRowPublished = (row) => PUBLISHED_STATES.includes(row.state);

const matchesAlignmentFilter = (row, filter) => filter === ALIGNMENT_FILTER.ALL || row.state === filter;

/** The rows the chip and the search leave, both on the whole comparison. */
export const filterAlignmentRows = (rows, { search = '', state = ALIGNMENT_FILTER.ALL } = {}) => {
  const needle = normalizeText(search.trim());
  return rows.filter(
    (row) =>
      matchesAlignmentFilter(row, state) &&
      (!needle || normalizeText(row.sku).includes(needle) || normalizeText(row.title).includes(needle))
  );
};

/** How many rows each chip holds. */
export const alignmentCounts = (rows) =>
  rows.reduce(
    (counts, row) => (row.state in counts ? { ...counts, [row.state]: counts[row.state] + 1 } : counts),
    Object.values(ALIGNMENT_STATE).reduce((counts, state) => ({ ...counts, [state]: 0 }), { all: rows.length })
  );

/** The states the store leaves out show a chip only while some row is in them, or while it is the filter in use. */
const OPTIONAL_CHIPS = [ALIGNMENT_STATE.NOT_STOCKED, ALIGNMENT_STATE.VIRTUAL_KIT, ALIGNMENT_STATE.PAUSED];

/** The chips over the rows, one per state. */
export const alignmentFilterOptions = (counts, active) =>
  Object.values(ALIGNMENT_STATE)
    .filter((state) => !OPTIONAL_CHIPS.includes(state) || counts[state] > 0 || active === state)
    .map((state) => ({ value: state, label: STATE_BADGE[state].label, count: counts[state], tone: STATE_BADGE[state].tone }));

/**
 * What confirming the comparison does, said before it goes: what the store
 * takes from then on, what keeps it so, and what it leaves out.
 */
export const applyConfirmMessage = ({ summary, location }) => {
  const where = location?.name ? ` en «${location.name}»` : '';
  const moves = storeMovesText(summary);
  const left = leftOutParts(summary);
  return [
    `Desde ahora, la tienda toma el número de Ninja${where}${moves ? `: ${moves}` : ''}.`,
    'Un cambio a mano en Shopify vuelve al número de Ninja, y las ventas de la tienda que Ninja no despacha se descuentan de su stock libre.',
    left.length ? `${leftOutLead(summary)}: ${listText(left)}.` : null,
  ]
    .filter(Boolean)
    .join(' ');
};

/** «Sube 3», «Baja 1», «Igual», «Pausado»: what confirming does to the row. */
export const alignmentStateText = (row) => {
  const label = STATE_BADGE[row.state]?.label ?? row.state_label;
  if (row.state === ALIGNMENT_STATE.UP || row.state === ALIGNMENT_STATE.DOWN) {
    return `${label} ${formatQuantity(Math.abs(row.difference ?? 0))}`;
  }
  return label;
};

export const alignmentStateTone = (row) => STATE_BADGE[row.state]?.tone ?? 'neutral';

/** «4 → 12»: what the store has now, and what it takes from Ninja; «—» when it takes nothing. */
export const alignmentPublishText = (row) => {
  if (!isAlignmentRowPublished(row)) return '—';
  if (row.state === ALIGNMENT_STATE.SAME) return formatQuantity(row.ninja);
  return `${formatQuantity(row.store)} → ${formatQuantity(row.ninja)}`;
};

/** Why «Confirmar» cannot go now, or `null`. Following the store's own sales needs its orders. */
export const applyBlocker = ({ detail, previewState }) => {
  if (needsAuthorization(detail) || alignmentScopesProblemOf(detail)) return 'La tienda tiene que volver a autorizar a Ninja';
  if (previewState === PREVIEW_STATE.APPLIED) return 'Esta comparación ya se confirmó';
  if (previewState === PREVIEW_STATE.EXPIRED) return 'La comparación venció: compara de nuevo';
  if (previewState !== PREVIEW_STATE.READY) return 'Todavía no hay una comparación lista';
  return null;
};

/* The store's own sales, whose order never reached Ninja. */

/** «12 ventas descontadas · 2 con faltante»: the sales Ninja holds stock for now. */
export const storeSalesSummary = (storeSales) =>
  [
    counted(storeSales?.discounted, 'venta descontada', 'ventas descontadas'),
    storeSales?.short ? counted(storeSales.short, 'con faltante', 'con faltante') : null,
  ]
    .filter(Boolean)
    .join(' · ');

const STORE_SALE_TONE = {
  [STORE_SALE_FILTER.DISCOUNTED]: 'info',
  [STORE_SALE_FILTER.SETTLED]: 'neutral',
  [STORE_SALE_FILTER.RETURNED]: 'success',
};

export const storeSaleTone = (row) => STORE_SALE_TONE[row.status] ?? 'neutral';

/** The chips over the store's sales. The server only counts the ones Ninja holds stock for now. */
export const storeSaleFilterOptions = (storeSales) => [
  {
    value: STORE_SALE_FILTER.DISCOUNTED,
    label: 'Descontadas',
    count: storeSales?.discounted,
    tone: STORE_SALE_TONE[STORE_SALE_FILTER.DISCOUNTED],
  },
  { value: STORE_SALE_FILTER.SETTLED, label: 'Cerradas en la tienda', tone: STORE_SALE_TONE[STORE_SALE_FILTER.SETTLED] },
  { value: STORE_SALE_FILTER.RETURNED, label: 'Devueltas', tone: STORE_SALE_TONE[STORE_SALE_FILTER.RETURNED] },
];

/** «La orden llegó a Ninja»: why Ninja gave a sale back, or `null`. */
export const storeSaleReasonText = (row) =>
  row.return_reason_label ? `${row.return_reason_label.charAt(0).toUpperCase()}${row.return_reason_label.slice(1)}` : null;

/** «2 de 3»: what Ninja took of what the store sold. */
export const storeSaleTakenText = (row) => `${formatQuantity(row.taken)} de ${formatQuantity(row.quantity)}`;

/** What the free stock did not cover of a sale, or `null`: Ninja took what there was. */
export const storeSaleShortText = (row) => {
  if (!row.short) return null;
  return row.short === 1 ? 'Faltó 1 unidad libre' : `Faltaron ${formatQuantity(row.short)} unidades libres`;
};
